<template>
  <ion-page class="bg-primary-default">
    <ion-content color="primary">
      <div class="main-container">
        <div class="h-full pt-32">
          <div class="px-16 fixed left-0 right-0 top-0 bg-primary-default z-10 pb-16">
            <div class="main-container">
              <Segment
                default-value="report"
                :segment-list="[{ label: 'Anmäl händelse', value: 'report'}, { label: 'Mina anmälningar', value: 'history'}]"
                @change-segment="changeSection"
              />
            </div>
          </div>
          <swiper
            :slides-per-view="1"
            :allow-touch-move="false"
            :initial-slide="0"
            @swiper="setSwiperInstance"
          >
            <swiper-slide>
              <div class="template-grid w-full px-16">
                <div
                  v-for="template in templates"
                  :key="template.id"
                  class="flex flex-col justify-center items-center aspect-square bg-primary-background cursor-pointer transition-all rounded-md"
                  :class="{
                    'text-white': !backgroundDarkText,
                    'text-black': backgroundDarkText,
                  }"
                  @click="nextStep(template.id)"
                >
                  <i
                    class="sh-icon text-[40px] mb-16"
                    :class="{
                      [`sh-icon-${template.icon}`]: template.icon
                    }"
                  />
                  <div class="text-center px-8">{{ template.name }}</div>
                </div>
              </div>
            </swiper-slide>
            <swiper-slide>
              <div v-if="currentUser" class="py-24 w-full">
                <div class="px-16 text-dark mb-24 pb-24 border-b border-secondary-default">
                  <TextInput
                    class="bg-white rounded-md"
                    :dark-background="true"
                    placeholder="Sök på adress eller händelse..."
                    :is-search-input="true"
                    :value="incidentsSearchQuery"
                    @on-input="searchInput"
                  />
                  <ion-toggle
                    v-if="[RoleHierarchy.SecurityAdminUser].includes(currentUser.role.hierarchy)"
                    class="w-full mt-24"
                    aria-label="Tertiary toggle"
                    color="tertiary"
                    justify="space-between"
                    :checked="listPersonalIncidents"
                    @ion-change="(e) => listPersonalIncidents = e.detail.checked"
                  >
                    <div class="headline-small text-white">Visa bara mina anmälningar</div>
                  </ion-toggle>
                  <ion-toggle
                    class="w-full mt-24"
                    aria-label="Hide drafts toggle"
                    color="tertiary"
                    justify="space-between"
                    :checked="hideDrafts"
                    @ion-change="(e) => hideDrafts = e.detail.checked"
                  >
                    <div class="headline-small text-white">Dölj utkast</div>
                  </ion-toggle>
                </div>
                <div class="overflow-auto">
                  <div v-if="myDrafts.length && !hideDrafts">
                    <div class="headline-lg relative px-16 mb-16 customizable-text">
                      Utkast
                      <span class="base-xs absolute top-2 ml-4">{{ myDrafts.length }}</span>
                    </div>
                    <div>
                      <IncidentHistoryItem
                        v-for="(incidentItem, i) in myDrafts"
                        :key="incidentItem.id"
                        :index="i"
                        :incident-item="incidentItem"
                        @click="resumeDraft(incidentItem)"
                      />
                    </div>
                    <Button
                      v-if="incidentStore.myDraftsNextPage"
                      class="m-16"
                      type="light"
                      :text="'Visa fler utkast'"
                      @click="incidentStore.fetchMoreDrafts"
                    />
                  </div>
                  <div v-if="myIncidents.length">
                    <div class="headline-lg relative px-16 mb-16 mt-24 customizable-text">
                      Anmälningar
                      <span class="base-xs absolute top-2 ml-4">{{ myIncidents.length }}</span>
                    </div>
                    <div>
                      <IncidentHistoryItem
                        v-for="(incidentItem, i) in myIncidents"
                        :key="incidentItem.id"
                        :index="i"
                        :incident-item="incidentItem"
                        @click="resumeDraft(incidentItem)"
                      />
                    </div>
                    <Button
                      v-if="incidentStore.myIncidentsNextPage"
                      class="m-16"
                      type="light"
                      :text="'Visa fler anmälningar'"
                      @click="incidentStore.fetchMoreIncidents"
                    />
                  </div>
                  <div v-if="myIncidents.length === 0 && (myDrafts.length === 0 || hideDrafts)" class="headline-lg relative px-16 mb-16">
                    Du har inga tidigare anmälningar
                  </div>
                </div>
              </div>
              <div v-else class="py-24 w-full px-16 text-center">
                Du måste vara inloggad för att kunna se tidigare anmälningar
                <Button
                  class="mt-16"
                  type="light"
                  :text="'Logga in / Skapa konto'"
                  @click="goToSignPage"
                />
              </div>
            </swiper-slide>
          </swiper>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import {
  IonPage,
  IonContent,
  IonToggle,
  SegmentChangeEventDetail, useIonRouter
} from '@ionic/vue';
import { useIncidentStore } from '@/store/incident';
import { storeToRefs } from 'pinia';
import { nextTick, ref, watch } from 'vue';
import Segment from '@/components/Segment.vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import 'swiper/css';
import '@ionic/vue/css/ionic-swiper.css';
import IncidentHistoryItem from '@/components/incident-modal/IncidentHistoryItem.vue';
import { Incident } from '@/constants/types/api';
import Button from '@/components/Button.vue';
import TextInput from '@/components/form-elements/TextInput.vue';
import RoleHierarchy from '@/constants/role-hierarchy';
import { useUserStore } from '@/store/user';

const swiperRef = ref();

const setSwiperInstance = (swiper: any) => {
  swiperRef.value = swiper;
};

const incidentStore = useIncidentStore();
const userStore = useUserStore();
const ionRouter = useIonRouter();

const { templates, myIncidents, myDrafts } = storeToRefs(incidentStore);
const { currentUser, backgroundDarkText } = storeToRefs(userStore);
const { openHistoryTab } = storeToRefs(incidentStore);

const selectedSection = ref('report');
const incidentsSearchQuery = ref('');
const listPersonalIncidents = ref(true);
const hideDrafts = ref(false);

const nextStep = async(id: number) => {
  await incidentStore.fetchIncident({ templateId: id });
  incidentStore.onNext();
};

const changeSection = (input: { detail: SegmentChangeEventDetail }) => {
  selectedSection.value = input.detail.value as string;
  if (input.detail.value === 'history') {
    swiperRef.value.slideTo(1);
  } else {
    swiperRef.value.slideTo(0);
  }
};

const resumeDraft = async(incident: Incident) => {
  await incidentStore.fetchIncident({ incidentId: incident.id });
  incidentStore.onNext();
};

type timer = ReturnType<typeof setTimeout>;
let debounce: timer;
const searchInput = (input: string) => {
  incidentsSearchQuery.value = input;
  if (debounce) {
    clearTimeout(debounce);
  }
  debounce = setTimeout(async() => {
    await incidentStore.onMyIncidentSearch(input);
  }, 1000);
};

const goToSignPage = () => {
  incidentStore.closeIncidentModal();
  ionRouter.push('/auth');
};

watch(
  listPersonalIncidents,
  () => {
    incidentStore.filterMyIncidents(listPersonalIncidents.value);
  }
);

watch(
  openHistoryTab,
  async(value) => {
    if (value) {
      selectedSection.value = 'history';
      await nextTick();
      if (swiperRef.value?.slideTo) {
        swiperRef.value.slideTo(1);
      }
      incidentStore.setOpenHistoryTab(false);
    }
  },
  { immediate: true }
);

</script>

<style scoped>

.template-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-top: 32px;
  padding-bottom: 24px;

  @screen desk {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.swiper {
  width: 100%;
  height: auto;
}
</style>
